import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import StatCard from './ui/StatCard'
import Section from './ui/Section'
import Button from './ui/Button'
import GetAppButton from './GetAppButton'
import { HERO_STATS } from '../data/constants.jsx'
import { useIcon } from '../hooks/useIcons.jsx'
import { useAuth } from '../contexts/AuthContext'
import apiService from '../services/api'

const HeroSection = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuth()
  const [stats, setStats] = useState(HERO_STATS)
  const [loading, setLoading] = useState(true)

  // Handler for scrolling to Q&A section
  const scrollToQA = () => {
    const qaSection = document.querySelector('[data-section="qa"]')
    if (qaSection) {
      qaSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // Handler for joining (navigate to login)
  const handleJoinNow = () => {
    navigate('/login')
  }

  useEffect(() => {
    const loadStatistics = async () => {
      try {
        const data = await apiService.request('/leaderboard/stats')
        
        // Update stats with real data
        const updatedStats = HERO_STATS.map(stat => {
          // Handle nested object paths like 'users.total_registered'
          const keys = stat.key.split('.')
          let value = data
          for (const key of keys) {
            value = value?.[key]
          }
          return {
            ...stat,
            value: value?.toLocaleString() || '0'
          }
        })
        
        setStats(updatedStats)
      } catch (error) {
        console.error('Failed to load statistics:', error)
        // Keep default values if API fails
      } finally {
        setLoading(false)
      }
    }

    loadStatistics()
  }, [])

  return (
    <section className="relative py-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-dawn opacity-50"></div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-4 font-arabic">
              {t('hero.arabicTitle')}
            </h1>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
              {t('hero.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {t('hero.subtitle')}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            {!user && (
              <Button size="lg" onClick={handleJoinNow}>{t('hero.getStarted')}</Button>
            )}
            <Button variant="secondary" size="lg" onClick={scrollToQA}>{t('hero.learnMore')}</Button>
          </div>

          {/* Below the sign-up buttons, not beside them: joining is the first
              step and installing the app is the second, so the order on the
              page matches the order a member does them in. */}
          <div className="flex flex-col items-center gap-3 mb-16">
            <p className="text-sm text-muted-foreground">{t('app.downloadSubtitle')}</p>
            <GetAppButton />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {loading ? (
              // Loading skeleton
              [...Array(3)].map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="bg-gray-200 rounded-lg h-24 w-full"></div>
                </div>
              ))
            ) : (
              stats.map((stat, index) => (
                <StatCard
                  key={index}
                  icon={useIcon(stat.icon)}
                  value={stat.value}
                  label={stat.label}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection